// src/contexts/GameContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  const { currentUser } = useAuth();
    const { 
    isConnected, 
    socket,
    // Game action methods
        drawCard: wsDrawCard, 
    discardCard: wsDiscardCard, 
    formMeld: wsFormMeld, 
    declareRummy: wsDeclareRummy,
    startNextDeal: wsStartNextDeal,
    autoArrange: wsAutoArrange,
    dropFromGame: wsDropFromGame,
    // Game room methods
    leaveGame: wsLeaveGame, 
    // Free Play methods
    quickJoinFreePlay: wsQuickJoinFreePlay,
    getFreePlayStatus: wsGetFreePlayStatus,
    leaveFreePlay: wsLeaveFreePlay,
    setFreePlayReady: wsSetFreePlayReady,
    startFreePlayGame: wsStartFreePlayGame,
    // Player actions
    setPlayerReady: wsSetPlayerReady,
    // Voice chat methods
    joinVoiceChat: wsJoinVoiceChat,
    leaveVoiceChat: wsLeaveVoiceChat,
    toggleVoiceMute: wsToggleVoiceMute,
    getVoiceParticipants: wsGetVoiceParticipants,
    sendVoiceSignal: wsSendVoiceSignal,
    sendVoiceActivity: wsSendVoiceActivity,
    // Reconnection
    reconnectToGame: wsReconnectToGame,
    // NEW: Game state ref updater (provided by WebSocketContext)
    updateGameStateRef
  } = useWebSocket();
  
  // Available currencies
  const AVAILABLE_CURRENCIES = ['USD', 'LRD', 'INR'];
  
  // Game state
    const [currentGame, setCurrentGame] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [stateHash, setStateHash] = useState(null);
  const [players, setPlayers] = useState([]);
  const [userCards, setUserCards] = useState([]);
  const [currentTurn, setCurrentTurn] = useState(null);
  const [gameStatus, setGameStatus] = useState('waiting');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Point table / declaration result state
  const [pointTable, setPointTable] = useState(null);
  const [declarationMessage, setDeclarationMessage] = useState('');
  const [gameOver, setGameOver] = useState(false);
  const [dealInfo, setDealInfo] = useState(null); // { currentDeal, totalDeals }
  
  const setCurrentGameId = useCallback((gameId) => {
    if (!gameId) return;
    setCurrentGame(prev => {
      if (prev && prev.id === gameId) return prev;
      return { id: gameId, game_type: 'free_play' };
    });
  }, []);
  
  // Free play state
  const [freePlayRooms, setFreePlayRooms] = useState([]);
  const [isFreePlayMode, setIsFreePlayMode] = useState(false);
  const [freePlayTimer, setFreePlayTimer] = useState(90);
  
  // Cash game state
  const [cashGames, setCashGames] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  
  // Custom rooms state
  const [customRooms, setCustomRooms] = useState([]);
  
  // Voice chat state
  const [voiceParticipants, setVoiceParticipants] = useState([]);
  const [isInVoiceChat, setIsInVoiceChat] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [voiceChatRoomId, setVoiceChatRoomId] = useState(null);
  
        // Refs for polling and cleanup
    const freePlayPollingInterval = useRef(null);
    const freePlayTimerInterval = useRef(null);
    const lastActionTime = useRef(Date.now());
    
    // 🔧 Ref to preserve grouped card structure across server updates
    const preservedCardsRef = useRef(null);

  // ==================== UPDATE GAME STATE REF IN WEBSOCKET CONTEXT ====================
  useEffect(() => {
    if (gameState && updateGameStateRef) {
            updateGameStateRef(gameState);
    }
  }, [gameState, updateGameStateRef]);

  // ==================== GAME ACTION METHODS ====================

      const drawCard = async (fromDeck = true) => {
     console.log('📤 GameContext.drawCard called, fromDeck =', fromDeck, 'gameId =', currentGame?.id, 'userId =', currentUser?.id);

    if (!currentGame || !currentGame.id) {
      setError('Not in a game');
      return { success: false, error: 'Not in a game' };
    }
    
    if (!isMyTurn) {
      setError('Not your turn');
      return { success: false, error: 'Not your turn' };
    }
    
    if (Date.now() - lastActionTime.current < 1000) {
      setError('Please wait before taking another action');
      return { success: false, error: 'Action too soon' };
    }
    
        if (!gameState?.stateHash) {
      setError('Game not fully loaded – please wait');
      return { success: false, error: 'Game not ready' };
    }
    
    setLoading(true);
    setError(null);
    lastActionTime.current = Date.now();
    console.log('🎯 Sending draw with stateHash:', gameState?.stateHash);
    
    try {
      console.log('🎴 Drawing card from:', fromDeck ? 'deck' : 'discard');
      const result = await wsDrawCard(currentGame.id, fromDeck, gameState?.stateHash);
      console.log('📥 wsDrawCard result:', result);
      
      if (result.success) {
        setStateHash(result.stateHash);
        return result;
      } else {
                        // Benign errors: do not set global error
        if (result.error === 'You have already drawn a card this turn' || result.error === 'Draw already in progress' || result.error === 'Draw request already in progress') {
          console.log('Duplicate draw ignored (expected)');
        } else if (result.error === 'Cannot draw a printed joker from the discard pile') {
          console.log('Printed joker draw blocked (benign error)');
        } else {
          setError(result.error || 'Failed to draw card');
        }
        return result;
      }
            } catch (err) {
      console.error('❌ Error drawing card:', err);
            // Also check for the specific message in the caught error
      if (err.message === 'You have already drawn a card this turn' || err.message === 'Draw already in progress' || err.message === 'Draw request already in progress') {
        console.log('Duplicate draw ignored (expected)');
      } else if (err.message === 'Cannot draw a printed joker from the discard pile') {
        console.log('Printed joker draw blocked (benign error)');
      } else {
        setError(err.message);
      }
      return { success: false, error: err.message };
        } finally {
      setLoading(false);
    }
  };

    const discardCard = async (cardId) => {
    console.log('📤 GameContext.discardCard called, cardId =', cardId, 'gameId =', currentGame?.id, 'userId =', currentUser?.id);

    if (!currentGame || !currentGame.id) {
      setError('Not in a game');
      return { success: false, error: 'Not in a game' };
    }
    
    if (!isMyTurn) {
      setError('Not your turn');
      return { success: false, error: 'Not your turn' };
    }
    
    if (!cardId) {
      setError('No card selected');
      return { success: false, error: 'No card selected' };
    }
    
    if (Date.now() - lastActionTime.current < 1000) {
      setError('Please wait before taking another action');
      return { success: false, error: 'Action too soon' };
    }
    
        if (!gameState?.stateHash) {
      setError('Game not fully loaded – please wait');
      return { success: false, error: 'Game not ready' };
    }
    
    setLoading(true);
    setError(null);
    lastActionTime.current = Date.now();
    
    try {
      console.log('🗑️ Discarding card:', cardId);
      const result = await wsDiscardCard(currentGame.id, cardId, gameState?.stateHash);
      
      if (result.success) {
        setStateHash(result.stateHash);
        return result;
      } else {
                // Benign errors: do not set global error
        // Added 'Discard already in progress', 'Discard request already in progress' and 'Discard too soon, please wait' to benign list
        const benignErrors = [
          'Card not found in player hand',
          'Not your turn',
          'You have already drawn a card this turn',
          'Discard already in progress',
          'Discard request already in progress',
          'Discard too soon, please wait'
        ];
        if (benignErrors.includes(result.error)) {
          console.log('Benign discard error:', result.error);
        } else {
          setError(result.error || 'Failed to discard card');
        }
        return result;
      }
        } catch (err) {
      console.error('❌ Error discarding card:', err);
            const benignErrors = [
        'Card not found in player hand',
        'Not your turn',
        'You have already drawn a card this turn',
        'Discard already in progress',
        'Discard request already in progress',
        'Discard too soon, please wait'
      ];
      if (benignErrors.includes(err.message)) {
        console.log('Benign discard error (caught):', err.message);
      } else {
        setError(err.message);
      }
      return { success: false, error: err.message };
        } finally {
      setLoading(false);
    }
  };

  const formMeld = async (cards, meldType = 'group') => {
    if (!currentGame || !currentGame.id) {
      setError('Not in a game');
      return { success: false, error: 'Not in a game' };
    }
    
    if (!isMyTurn) {
      setError('Not your turn');
      return { success: false, error: 'Not your turn' };
    }
    
    if (!cards || !cards.length) {
      setError('No cards selected');
      return { success: false, error: 'No cards selected' };
    }
    
    if (cards.length < 3) {
      setError('Need at least 3 cards for a meld');
      return { success: false, error: 'Need at least 3 cards for a meld' };
    }
    
    if (Date.now() - lastActionTime.current < 1000) {
      setError('Please wait before taking another action');
      return { success: false, error: 'Action too soon' };
    }
    
    if (!gameState?.stateHash) {
      setError('Game not fully loaded – please wait');
      return { success: false, error: 'Game not ready' };
    }
    
    setLoading(true);
    setError(null);
    lastActionTime.current = Date.now();
    
    try {
      console.log('🃏 Forming meld:', { cards, meldType });
      const result = await wsFormMeld(currentGame.id, cards, meldType, gameState?.stateHash);
      
      if (result.success) {
        setStateHash(result.stateHash);
        return result;
      } else {
        setError(result.error || 'Failed to form meld');
        return result;
      }
    } catch (err) {
      console.error('❌ Error forming meld:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

        const declareRummy = async (sets = [], runs = [], discardedCardId = null) => {
    if (!currentGame || !currentGame.id) {
      setError('Not in a game');
      return { success: false, error: 'Not in a game' };
    }
    
    if (!isMyTurn) {
      setError('Not your turn');
      return { success: false, error: 'Not your turn' };
    }
    
    if (Date.now() - lastActionTime.current < 1000) {
      setError('Please wait before taking another action');
      return { success: false, error: 'Action too soon' };
    }
    
    if (!gameState?.stateHash) {
      setError('Game not fully loaded – please wait');
      return { success: false, error: 'Game not ready' };
    }
    
    if (!discardedCardId) {
      setError('You must select a card to discard when declaring');
      return { success: false, error: 'No discard card selected' };
    }
    
    setLoading(true);
    setError(null);
    lastActionTime.current = Date.now();
    
    try {
      console.log('🏆 Declaring rummy with', sets.length, 'sets and', runs.length, 'runs, discarding card', discardedCardId);
      const result = await wsDeclareRummy(currentGame.id, gameState?.stateHash, sets, runs, discardedCardId);
      
            if (result.success) {
        setStateHash(result.stateHash);
        // Don't set gameStatus here — socket events (rummy-declared, game-state-update) 
        // will set the correct status ('submission' or 'finished') based on server flow.
        // Setting it to 'finished' prematurely would hide the submission phase UI.
        return result;
      } else {
        setError(result.error || 'Failed to declare');
        return result;
      }
        } catch (err) {
      console.error('❌ Error declaring rummy:', err);
      // Check if the error has a full response attached (from emitWithPromise)
      // with server-provided pointTable data (wrong declaration with game over)
      const responseData = err.response || {};
      if (responseData.pointTable) {
        // Wrong declaration that ended the game — pointTable data is available
        return { 
          success: false, 
          error: err.message, 
          pointTable: responseData.pointTable, 
          gameOver: responseData.gameOver !== false,
          ...responseData 
        };
      }
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const autoArrange = async () => {
    if (!currentGame || !currentGame.id) {
      setError('Not in a game');
      return { success: false, error: 'Not in a game' };
    }
    
    if (!isMyTurn) {
      setError('Not your turn');
      return { success: false, error: 'Not your turn' };
    }
    
    if (Date.now() - lastActionTime.current < 1000) {
      setError('Please wait before taking another action');
      return { success: false, error: 'Action too soon' };
    }
    
    if (!gameState?.stateHash) {
      setError('Game not fully loaded – please wait');
      return { success: false, error: 'Game not ready' };
    }
    
    setLoading(true);
    setError(null);
    lastActionTime.current = Date.now();
    
    try {
      console.log('🃏 Auto-arranging cards');
      const result = await wsAutoArrange(currentGame.id, gameState?.stateHash);
      
      if (result.success) {
        setStateHash(result.stateHash);
        return result;
      } else {
        setError(result.error || 'Failed to auto-arrange');
        return result;
      }
    } catch (err) {
      console.error('❌ Error auto-arranging cards:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const dropFromGame = async () => {
    if (!currentGame || !currentGame.id) {
      setError('Not in a game');
      return { success: false, error: 'Not in a game' };
    }
    
    if (!isMyTurn) {
      setError('Can only drop on your turn');
      return { success: false, error: 'Not your turn' };
    }
    
    if (!gameState?.stateHash) {
      setError('Game not fully loaded – please wait');
      return { success: false, error: 'Game not ready' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('💔 Dropping from game');
      const result = await wsDropFromGame(currentGame.id, gameState?.stateHash);
      
      if (result.success) {
        setStateHash(result.stateHash);
        return result;
      } else {
        setError(result.error || 'Failed to drop');
        return result;
      }
    } catch (err) {
      console.error('❌ Error dropping from game:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // ==================== VOICE CHAT METHODS ====================

  const joinVoiceChat = async () => {
    if (!currentGame || !currentGame.id) {
      setError('Not in a game');
      return { success: false, error: 'Not in a game' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🎤 Joining voice chat');
      const result = await wsJoinVoiceChat(currentGame.id);
      
      if (result.success) {
        setIsInVoiceChat(true);
        setVoiceChatRoomId(result.roomId);
        return result;
      } else {
        setError(result.error || 'Failed to join voice chat');
        return result;
      }
    } catch (err) {
      console.error('❌ Error joining voice chat:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const leaveVoiceChat = async () => {
    if (!currentGame || !currentGame.id) {
      return { success: false, error: 'Not in a game' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🎤 Leaving voice chat');
      const result = await wsLeaveVoiceChat(currentGame.id);
      
      if (result.success) {
        setIsInVoiceChat(false);
        setVoiceChatRoomId(null);
        setVoiceParticipants([]);
        return result;
      } else {
        setError(result.error || 'Failed to leave voice chat');
        return result;
      }
    } catch (err) {
      console.error('❌ Error leaving voice chat:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const toggleVoiceMute = async (targetPlayerId = null) => {
    if (!currentGame || !currentGame.id) {
      return { success: false, error: 'Not in a game' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🎤 Toggling voice mute:', targetPlayerId);
      const result = await wsToggleVoiceMute(currentGame.id, targetPlayerId);
      
      if (result.success) {
        setIsMuted(result.isMuted);
        return result;
      } else {
        setError(result.error || 'Failed to toggle mute');
        return result;
      }
    } catch (err) {
      console.error('❌ Error toggling voice mute:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const getVoiceParticipants = async () => {
    if (!currentGame || !currentGame.id) {
      return { success: false, error: 'Not in a game' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🎤 Getting voice participants');
      const result = await wsGetVoiceParticipants(currentGame.id);
      
      if (result.success) {
        setVoiceParticipants(result.participants || []);
        return result;
      } else {
        setError(result.error || 'Failed to get voice participants');
        return result;
      }
    } catch (err) {
      console.error('❌ Error getting voice participants:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const sendVoiceSignal = async (targetId, signal) => {
    if (!currentGame || !currentGame.id) {
      return { success: false, error: 'Not in a game' };
    }
    
    try {
      console.log('📡 Sending voice signal to:', targetId);
      return await wsSendVoiceSignal(currentGame.id, targetId, signal);
    } catch (err) {
      console.error('❌ Error sending voice signal:', err);
      return { success: false, error: err.message };
    }
  };

  const sendVoiceActivity = async (isSpeaking) => {
    if (!currentGame || !currentGame.id) {
      return { success: false, error: 'Not in a game' };
    }
    
    try {
      console.log('🎤 Voice activity:', isSpeaking ? 'speaking' : 'silent');
      return await wsSendVoiceActivity(currentGame.id, isSpeaking);
    } catch (err) {
      console.error('❌ Error sending voice activity:', err);
      return { success: false, error: err.message };
    }
  };

  // ==================== RECONNECTION METHODS ====================

  const reconnectToGame = async () => {
    if (!currentGame || !currentGame.id) {
      return { success: false, error: 'No game to reconnect to' };
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔗 Reconnecting to game:', currentGame.id);
      const result = await wsReconnectToGame(currentGame.id);
      
      if (result.success) {
        if (result.voiceChat && result.voiceChat.success) {
          setIsInVoiceChat(true);
          setVoiceChatRoomId(result.voiceChat.roomId);
        }
        return result;
      } else {
        setError(result.error || 'Failed to reconnect');
        return result;
      }
    } catch (err) {
      console.error('❌ Error reconnecting to game:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  // ==================== EVENT HANDLERS ====================

const handleGameStateInitial = useCallback((data) => {
    console.log('🎮 [INITIAL] Game state received:', data);
    setError(null);
    setGameState(data);
    setStateHash(data.stateHash);
    setPlayers(data.players || []);
    setCurrentTurn(data.currentTurnPlayerId);
    setGameStatus(data.status || 'playing');
    if (data.players && currentUser) {
      const player = data.players.find(p => String(p.userId) === String(currentUser.id));
      if (player && player.cards) {
        setUserCards(player.cards);
        // 🔧 Save initial cards to preserved ref (they're flat at this point)
        preservedCardsRef.current = [...player.cards];
      }
    }
  }, [currentUser]);

        const handleGameStateUpdate = useCallback((data) => {
    console.log('🔄 Game state updated:', data);
    // 🔧 CRITICAL FIX: server sends { gameState: {...} }, unwrap it
    const state = data.gameState || data;
    setError(null);
    if (state) {
      // 🛑 CRITICAL FIX: Deep clone before modifying to avoid mutating the original
      // socket data that other handlers (e.g. EnhancedRummyTable) also reference.
      const clonedState = JSON.parse(JSON.stringify(state));
      
      // 🔧 Preserve grouped card structure for the current user
      // 🛑 FIX: Only override if preservedCardsRef has actual cards (> 0 length).
      // An empty array (after updatePreservedCards([]) on new deal transition)
      // should NOT override the server's fresh cards.
      if (clonedState.players && currentUser && preservedCardsRef.current && preservedCardsRef.current.length > 0) {
        clonedState.players = clonedState.players.map(p =>
          String(p.userId) === String(currentUser.id)
            ? { ...p, cards: preservedCardsRef.current }
            : p
        );
      }
      
      setGameState(prev => ({
        ...clonedState,
        // Preserve transient flags that are not in the full game state snapshot
        wasReshuffled: prev?.wasReshuffled || clonedState.wasReshuffled || false
      }));
      setStateHash(clonedState.stateHash);
      setPlayers(clonedState.players || []);
      setCurrentTurn(clonedState.currentTurnPlayerId);   // now correctly extracted
      setGameStatus(clonedState.status || 'playing');

      // Set userCards from the preserved structure if available
      if (clonedState.players && currentUser) {
        const player = clonedState.players.find(p => 
          String(p.userId) === String(currentUser.id) || 
          String(p.id) === String(currentUser.id)
        );
        if (player && player.cards) {
          setUserCards(player.cards);
        }
      }
    }
  }, [currentUser]);

  const handleCardDrawn = useCallback((data) => {
    console.log('🎴 Card drawn:', data);
    if (data) {
      if (data.stateHash) setStateHash(data.stateHash);
      setGameState(prev => ({
        ...prev,
        deckCount: data.deckCount,
        discardTop: data.discardTop,
        currentTurnPlayerId: data.currentTurnPlayerId
      }));
      setCurrentTurn(data.currentTurnPlayerId);
    }
  }, []);

    const handleCardDiscarded = useCallback((data) => {
    console.log('🗑️ Card discarded:', data);
    if (data) {
      if (data.stateHash) setStateHash(data.stateHash);
      setGameState(prev => ({
        ...prev,
        discardTop: data.discardTop,
        currentTurnPlayerId: data.nextPlayerId,
        deckCount: data.deckCount ?? prev?.deckCount,
        wasReshuffled: data.wasReshuffled || false
      }));
      setCurrentTurn(data.nextPlayerId);
    }
  }, []);

  const handleMeldFormed = useCallback((data) => {
    console.log('🃏 Meld formed:', data);
    if (data && data.stateHash) setStateHash(data.stateHash);
  }, []);

  const handleTurnChanged = useCallback((data) => {
    console.log('🔄 Turn changed:', data);
    if (data) {
      if (data.stateHash) setStateHash(data.stateHash);
      setGameState(prev => ({
        ...prev,
        currentTurnPlayerId: data.currentTurnPlayerId
      }));
      setCurrentTurn(data.currentTurnPlayerId);
    }
  }, []);

    const handleGameEnded = useCallback((data) => {
    console.log('🏁 Game ended:', data);
    setGameStatus(data?.gameStatus || 'finished');
    if (data) {
      // Store point table data
      if (data.pointTable) {
        setPointTable(data.pointTable);
      }
      setDeclarationMessage(data.message || '');
      setGameOver(data.gameOver !== undefined ? data.gameOver : true);
      if (data.currentDeal !== undefined) {
        setDealInfo({ currentDeal: data.currentDeal, totalDeals: data.totalDeals });
      }

      setGameState(prev => ({
        ...prev,
        winner: data.winner,
        scores: data.scores,
        winningHand: data.winningHand,
        pointTable: data.pointTable,
        declarationMessage: data.message,
        gameOver: data.gameOver,
        currentDeal: data.currentDeal,
        totalDeals: data.totalDeals,
      }));
      if (data.gameOver && isInVoiceChat) {
        leaveVoiceChat();
      }
    }
  }, [isInVoiceChat]);

    const handlePlayerDropped = useCallback((data) => {
    console.log('💔 Player dropped:', data);
    if (data && String(data.playerId) === String(currentUser?.id)) {
      setError(`${data.reason === 'wrong_declaration' ? 'Invalid declaration' : 'Dropped'} — ${data.penalty} penalty points`);
      // Update game state to reflect dropped status and score
      setGameState(prev => {
        if (!prev?.players) return prev;
        const updatedPlayers = prev.players.map(p =>
          String(p.userId) === String(data.playerId)
            ? { ...p, dropped: true, score: data.newScore || p.score, cards: [] }
            : p
        );
        return { ...prev, players: updatedPlayers };
      });
    }
  }, [currentUser]);

  const handleStateHashMismatch = useCallback((data) => {
    console.error('🚨 State hash mismatch:', data);
    setError('Game state synchronization error. Please rejoin the game.');
    setTimeout(() => { reconnectToGame(); }, 2000);
  }, []);

  // Voice chat events
  const handleVoiceParticipantJoined = useCallback((data) => {
    console.log('🎤 Voice participant joined:', data);
    setVoiceParticipants(prev => [
      ...prev.filter(p => p.user_id !== data.userId),
      {
        user_id: data.userId,
        username: data.username,
        is_muted: false,
        is_speaking: false
      }
    ]);
  }, []);

  const handleVoiceParticipantLeft = useCallback((data) => {
    console.log('🎤 Voice participant left:', data);
    setVoiceParticipants(prev => prev.filter(p => p.user_id !== data.userId));
  }, []);

  const handleVoiceMuteChanged = useCallback((data) => {
    console.log('🎤 Voice mute changed:', data);
    setVoiceParticipants(prev => prev.map(p => 
      p.user_id === data.playerId 
        ? { ...p, is_muted: data.isMuted }
        : p
    ));
    if (data.playerId === currentUser?.id) {
      setIsMuted(data.isMuted);
    }
  }, [currentUser]);

  const handleVoiceActivityChanged = useCallback((data) => {
    console.log('🎤 Voice activity changed:', data);
    setVoiceParticipants(prev => prev.map(p => 
      p.user_id === data.userId 
        ? { ...p, is_speaking: data.isSpeaking }
        : p
    ));
  }, []);

  // ==================== SOCKET.IO EVENT SETUP ====================

    useEffect(() => {
    if (!isConnected || !socket) {
      console.log('⚠️ WebSocket not connected - skipping event setup');
      return;
    }

    console.log('🔗 Setting up Socket.io event handlers');
    
    // Updated: listen for 'game-state-update' instead of 'game-state'
    socket.on('game-state-initial', handleGameStateInitial);
    socket.on('game-state-update', handleGameStateUpdate);
    // Removed 'game-started' listener as server no longer emits it
    socket.on('card-drawn', handleCardDrawn);
    socket.on('card-discarded', handleCardDiscarded);
    socket.on('meld-formed', handleMeldFormed);
    socket.on('turn-changed', handleTurnChanged);
    socket.on('rummy-declared', handleGameEnded);
    socket.on('player-dropped', handlePlayerDropped);
    socket.on('state-hash-mismatch', handleStateHashMismatch);
    
    // Voice chat events
    socket.on('voice-participant-joined', handleVoiceParticipantJoined);
    socket.on('voice-participant-left', handleVoiceParticipantLeft);
    socket.on('voice-mute-changed', handleVoiceMuteChanged);
    socket.on('voice-activity-changed', handleVoiceActivityChanged);

    return () => {
      console.log('🧹 Cleaning up Socket.io event handlers');
      socket.off('game-state-initial', handleGameStateInitial);
      socket.off('game-state-update', handleGameStateUpdate);
      socket.off('card-drawn', handleCardDrawn);
      socket.off('card-discarded', handleCardDiscarded);
      socket.off('meld-formed', handleMeldFormed);
      socket.off('turn-changed', handleTurnChanged);
      socket.off('rummy-declared', handleGameEnded);
      socket.off('player-dropped', handlePlayerDropped);
      socket.off('state-hash-mismatch', handleStateHashMismatch);
      
      socket.off('voice-participant-joined', handleVoiceParticipantJoined);
      socket.off('voice-participant-left', handleVoiceParticipantLeft);
      socket.off('voice-mute-changed', handleVoiceMuteChanged);
      socket.off('voice-activity-changed', handleVoiceActivityChanged);
    };
  }, [
    isConnected, socket,
    handleGameStateInitial, handleGameStateUpdate, handleCardDrawn, 
    handleCardDiscarded, handleMeldFormed, handleTurnChanged,
    handleGameEnded, handlePlayerDropped, handleStateHashMismatch,
    handleVoiceParticipantJoined, handleVoiceParticipantLeft,
    handleVoiceMuteChanged, handleVoiceActivityChanged
  ]);

  // Auto-reconnect on mount
  useEffect(() => {
    if (currentGame && currentGame.id && isConnected) {
      reconnectToGame();
    }
  }, [isConnected]);

  // ==================== UTILITIES ====================

  const isMyTurn = String(currentTurn) === String(currentUser?.id) || 
                 (gameState && String(gameState.currentTurnPlayerId) === String(currentUser?.id));

  const isHost = currentGame?.creator_id === currentUser?.id;
  const isInGame = currentGame && players.some(p => p.id === currentUser?.id);
  const isFreePlay = currentGame?.game_type === 'free_play' || isFreePlayMode;
  const hasVoiceChat = gameState?.hasVoiceChat || currentGame?.game_type === 'custom';

  const resetGameState = useCallback(() => {
    setCurrentGame(null);
    setGameState(null);
    setStateHash(null);
    setPlayers([]);
    setUserCards([]);
    setCurrentTurn(null);
    setGameStatus('waiting');
    setPointTable(null);
    setDeclarationMessage('');
    setGameOver(false);
    setDealInfo(null);
    setIsInVoiceChat(false);
    setVoiceParticipants([]);
    setVoiceChatRoomId(null);
    setIsMuted(false);
    setError(null);
    
    if (freePlayPollingInterval.current) {
      clearInterval(freePlayPollingInterval.current);
      freePlayPollingInterval.current = null;
    }
    
    if (freePlayTimerInterval.current) {
      clearInterval(freePlayTimerInterval.current);
      freePlayTimerInterval.current = null;
    }
    
    setIsFreePlayMode(false);
    setFreePlayTimer(90);
  }, []);

  const value = {
    // State
    currentGame,
    gameState,
    stateHash,
    players,
    userCards,
    currentTurn,
    gameStatus,
    loading,
    error,
    isMyTurn,
    isHost,
    isInGame,
    isFreePlayMode,
    freePlayTimer,
    selectedCurrency,
    setCurrentGameId,    
    // Voice chat state
    voiceParticipants,
    isInVoiceChat,
    isMuted,
        voiceChatRoomId,
    hasVoiceChat,

    // Point table / declaration state
    pointTable,
    declarationMessage,
    gameOver,
    dealInfo,
    
    // Constants
    AVAILABLE_CURRENCIES,
    
    // Game Actions
    drawCard,
    discardCard,
    formMeld,
    declareRummy,
    autoArrange,
        dropFromGame,
    startNextDeal: async () => {
      if (!currentGame || !currentGame.id) {
        setError('Not in a game');
        return { success: false, error: 'Not in a game' };
      }
      setLoading(true);
      try {
        const result = await wsStartNextDeal(currentGame.id);
        if (result.success) {
          setPointTable(null);
          setDeclarationMessage('');
          setGameOver(false);
          setDealInfo(null);
          setGameStatus('playing');
          return result;
        } else {
          setError(result.error || 'Failed to start next deal');
          return result;
        }
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    
    // Voice Chat Actions
    joinVoiceChat,
    leaveVoiceChat,
    toggleVoiceMute,
    getVoiceParticipants,
    sendVoiceSignal,
    sendVoiceActivity,
    
    // Reconnection
    reconnectToGame,
    
        // Utility Functions
    setError,
    clearError: () => setError(null),
    resetGameState,
    socket,
    // 🔧 Method to update preserved cards (used by EnhancedRummyTable to sync grouped structure)
    updatePreservedCards: (cards) => {
      preservedCardsRef.current = cards;
    }
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};